import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Output,
    computed,
    effect,
    inject,
    input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkDrag, CdkDropList, CdkDropListGroup, moveItemInArray } from '@angular/cdk/drag-drop';
import type { PaymentElement, PaymentMethod } from '@integrations/payment-methods';
import {
    PaymentMethodStatus,
    mapAvailablePaymentMethods,
    mapPaymentMethodCurrencies,
} from '@integrations/payment-methods';
import type { PaymentWidgetDataFormType } from '@web/widgets/payment-form/type/payment-form-widget.type';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { paymentsList, paymentTypeNames } from '@common/constants/payment-form.const';

import { paymentElementFormGroup } from '@web/widgets/common/base/base-form/base-form.form';
import { UserFacade } from '@web/store/user/user.facade';
import { EPaymentType } from '@common/enums';

import { SpControlHandleComponent } from '@libs/ui/sp-control-handle/src/sp-control-handle.component';
import { findActivePaymentMethod, getDefaultPaymentElementData } from './web-payment-methods-collection.helpers';

@Component({
    selector: 'web-payment-methods-collection',
    templateUrl: './web-payment-methods-collection.component.html',
    styleUrls: ['./web-payment-methods-collection.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CdkDropList, CdkDrag, CdkDropListGroup, SpControlHandleComponent, TranslateModule],
})
export class WebPaymentMethodsCollectionComponent {
    readonly translateService = inject(TranslateService);
    private readonly userFacade = inject(UserFacade);

    private readonly isUserTariffFree = toSignal(this.userFacade.isUserTariffFree$, { initialValue: false });

    public readonly paymentElements = input.required<PaymentWidgetDataFormType['paymentElements']>();
    public readonly isShopCart = input<boolean>(false);

    public readonly paymentMethods = input.required<PaymentMethod[]>();
    public readonly isLoading = input.required<boolean>();
    public readonly isFailure = input.required<boolean>();

    @Output() public editActionEvent: EventEmitter<number> = new EventEmitter();
    @Output() public setPaymentElementEvent: EventEmitter<Partial<PaymentElement>> = new EventEmitter();
    @Output() public setPaymentElementsEvent: EventEmitter<Partial<PaymentElement>[]> = new EventEmitter();
    @Output() public setSkipHistoryEvent: EventEmitter<void> = new EventEmitter();

    public readonly availablePaymentMethods = computed(() => mapAvailablePaymentMethods(this.paymentMethods()));
    public readonly paymentMethodsCurrencies = computed(() => mapPaymentMethodCurrencies(this.paymentMethods()));

    constructor() {
        let initialized = false;
        effect(() => {
            const paymentMethods = this.paymentMethods();
            const paymentElements = this.paymentElements();

            if (initialized) return;

            // For a new shop cart on a free tariff only the custom payment method is allowed,
            // so it is created regardless of the configured (non-custom) payment methods.
            const isForcedCustom = this.isShopCart() && this.isUserTariffFree();

            if (!isForcedCustom && !paymentMethods?.length) return;
            if (paymentElements.at(0)?.value?.payment_method_id) {
                initialized = true;
                return;
            }

            const paymentMethod = isForcedCustom ? null : findActivePaymentMethod(paymentMethods);
            const defaultData = getDefaultPaymentElementData(paymentMethod);

            if (!paymentElements.value.length) {
                const form = paymentElementFormGroup(this.translateService);
                form.patchValue(defaultData);
                this.setSkipHistoryEvent.emit();
                paymentElements.push(form);
                initialized = true;
                return;
            }

            this.setSkipHistoryEvent.emit();
            paymentElements.at(0).patchValue(defaultData);
            initialized = true;
        });
    }

    public dropPaymentElementItem(event: CdkDragDrop<PaymentWidgetDataFormType['paymentElements']>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        const paymentElements = this.paymentElements();
        const buttons = paymentElements.getRawValue();
        moveItemInArray(buttons, event.previousIndex, event.currentIndex);

        paymentElements.setValue(buttons);
    }

    public addPaymentElement(): void {
        const paymentMethods = this.paymentMethods();
        const paymentElements = this.paymentElements();
        const customPaymentMethod: PaymentMethod = {
            type: EPaymentType.custom,
            id: 'custom_payment_id',
            name: paymentTypeNames[EPaymentType.custom],
            status: PaymentMethodStatus.active,
            settings: null,
            isDefault: false,
        };

        let paymentMethod: PaymentMethod;

        if (this.isShopCart() && this.isUserTariffFree()) {
            paymentMethod = customPaymentMethod;
        } else {
            const usedPaymentMethods = paymentElements.value.map((paymentElement) => paymentElement.type);
            paymentMethod =
                paymentMethods.find(
                    (method: PaymentMethod) =>
                        !usedPaymentMethods.includes(method.type) && method.status === PaymentMethodStatus.active
                ) ?? customPaymentMethod;
        }
        const form = paymentElementFormGroup(this.translateService);
        form.patchValue({
            type: paymentMethod.type,
            payment_method_id: paymentMethod.id,
            isEditableTotalCost: false,
            currency: this.paymentMethodsCurrencies()[paymentMethod.type] || 'USD',
            isActive: true,
        });

        paymentElements.push(form);
    }

    public removePaymentElement(index: number): void {
        this.paymentElements().removeAt(index);
    }

    public trackByPaymentElementIndex(index: number): number {
        return index;
    }

    public getPaymentElementSubText(paymentElement: PaymentElement): string {
        const available = this.availablePaymentMethods();
        if (!available || !available[paymentElement.type]) {
            return this.translateService.instant('payment_method_is_not_active');
        }

        if (!paymentElement.amount) {
            return this.translateService.instant('payment_amount_invalid');
        }

        return `${this.translateService.instant('control_price')} ${paymentElement.amount} ${paymentElement.currency}`;
    }

    public getPaymentElementLabel(paymentElementType: PaymentElement['type']): string {
        if (!paymentElementType && paymentElementType !== EPaymentType.custom) {
            return null;
        }

        return paymentsList[paymentElementType].label;
    }

    public getPaymentElementIcon(paymentElementType: PaymentElement['type']): string {
        if (!paymentElementType && paymentElementType !== EPaymentType.custom) {
            return null;
        }

        return `./assets/img/websites/payment-icons/sm/${paymentTypeNames[paymentElementType]}-sm.svg`;
    }
}
